import { useQuery } from "@apollo/client/react";
import { useLocalStorage } from "@rehooks/local-storage";
import { useMemo } from "react";

import { EmptystateSearchNoResultsColored } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import SearchInput from "components/SearchInput";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import { URL_SEARCH_KEY } from "constants/urlQueryKeys";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeader from "ds/components/PageInfoHeader";
import SegmentedControl from "ds/components/SegmentedControl";
import { AnalyticsPage } from "hooks/useAnalytics";
import useErrorHandle from "hooks/useErrorHandle";
import useURLParams from "hooks/useURLParams";
import { AnsibleHost } from "types/generated";
import { decodeURIParam } from "utils/urls";
import { LAYOUT_MODE_OPTIONS } from "views/Account/ConfigManagement/constants";
import ConfigManagementEmptyState from "views/Account/ConfigManagement/EmptyState";
import ConfigManagementTreeGrid from "views/Account/ConfigManagement/TreeGrid";
import { LayoutMode } from "views/Account/ConfigManagement/types";
import { createAnsibleNodesByHost } from "views/Account/ConfigManagement/utils";

import {
  DEFAULT_LAYOUT_MODE,
  FILTERS_ORDER_SETTINGS_KEY,
  LAYOUT_MODE_LOCAL_STORAGE_KEY,
} from "./constants";
import { GET_RUN_ANSIBLE_HOSTS } from "./gql";
import styles from "./styles.module.css";

type AnsibleRunTasksProps = {
  stackId: string;
  runId: string;
  analyticsPage?: AnalyticsPage;
};

const AnsibleRunTasks = ({ stackId, runId, analyticsPage }: AnsibleRunTasksProps) => {
  const urlParams = useURLParams();
  const searchQuery = decodeURIParam(urlParams.get(URL_SEARCH_KEY));
  const [layoutMode, setLayoutMode] = useLocalStorage<LayoutMode>(
    LAYOUT_MODE_LOCAL_STORAGE_KEY,
    DEFAULT_LAYOUT_MODE
  );

  const { data, loading, error } = useQuery<{ ansibleHosts: AnsibleHost[] }>(
    GET_RUN_ANSIBLE_HOSTS,
    {
      variables: { input: { stack: stackId, run: runId } },
    }
  );

  const filteredAnsibleHosts = useMemo(() => {
    const ansibleHosts = data?.ansibleHosts || [];
    const searchValue = searchQuery?.trim();
    const filteredAnsibleHosts = searchValue
      ? ansibleHosts.filter((host) => host.name.includes(searchValue))
      : ansibleHosts;
    return filteredAnsibleHosts;
  }, [data?.ansibleHosts, searchQuery]);

  const nodes = useMemo(
    () =>
      createAnsibleNodesByHost(
        filteredAnsibleHosts,
        layoutMode === LayoutMode.Diagram,
        location.pathname
      ),
    [filteredAnsibleHosts, layoutMode]
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data?.ansibleHosts) {
    // TODO: skeleton
    return (
      <>
        <PageInfoHeader title="Tasks" />
        <PageLoading />
      </>
    );
  }

  const hasNoFilteringResults = searchQuery && !filteredAnsibleHosts.length;
  const isPageEmpty = !searchQuery && !filteredAnsibleHosts.length;
  const hasAnsibleHosts = !!filteredAnsibleHosts?.length;

  return (
    <>
      <PageInfoHeader title="Tasks">
        <SearchInput
          placeholder={SEARCH_PLACEHOLDERS.ANSIBLE_RUN_TASKS}
          filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
        />
        <SegmentedControl
          optionClassName={styles.layoutSwitchOption}
          onChange={(value) => {
            setLayoutMode(value);
          }}
          value={layoutMode}
          options={LAYOUT_MODE_OPTIONS}
        />
      </PageInfoHeader>

      {hasNoFilteringResults && (
        <EmptyState
          title="No results"
          caption="Try different filters."
          icon={EmptystateSearchNoResultsColored}
          announce
        />
      )}

      {isPageEmpty && (
        <ConfigManagementEmptyState
          title="No Ansible tasks to show"
          caption="Task details for this run may no longer be available."
        />
      )}

      {hasAnsibleHosts && (
        <ConfigManagementTreeGrid
          chartClassName={styles.chart}
          // TODO [Ansible v2] add lazy loading
          loadMoreItems={async () => undefined}
          layoutMode={layoutMode}
          nodes={nodes}
          analyticsPage={analyticsPage}
        />
      )}
    </>
  );
};

export default AnsibleRunTasks;
